import { usePastSangamSessions } from '@/hooks/useFirestore'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { 
  History, 
  Users, 
  Calendar, 
  MessageSquare, 
  Clock,
  ArrowRight,
  Loader2,
  Mic2
} from 'lucide-react'

export default function RecordingsPage() {
  const { data: pastSessions, isLoading } = usePastSangamSessions(50) // Get more for the archive

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <History className="w-8 h-8 text-primary-500" />
            Podcast Archive
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-2xl">
            Explore the library of past Strategic Sangam discussions. Access full transcripts and analysis of how the Strategic Council agents are architecting the future.
          </p>
        </div>
        <div className="bg-primary-50 dark:bg-primary-900/20 px-4 py-2 rounded-lg border border-primary-100 dark:border-primary-900/30">
          <span className="text-sm font-bold text-primary-700 dark:text-primary-300">
            {pastSessions?.length || 0} Recorded Sessions
          </span>
        </div>
      </header>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500 mb-4" />
          <p className="text-gray-500">Loading library...</p>
        </div>
      ) : !pastSessions || pastSessions.length === 0 ? (
        <div className="text-center py-24 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <Mic2 className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-700" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">No recordings yet</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Past sessions will appear here once they are completed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pastSessions.map((session) => (
            <Link 
              key={session.id} 
              to={`/?id=${session.id}`}
              className="group bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-xl hover:border-primary-500/50 transition-all duration-300 flex flex-col h-full overflow-hidden"
            >
              {/* Card Header Image/Pattern */}
              <div className="h-32 bg-gradient-to-br from-gray-900 to-primary-900 relative p-6 flex items-start justify-between">
                <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-widest border border-white/20">
                  Recorded
                </div>
                <History className="w-12 h-12 text-white opacity-10 absolute -bottom-2 -right-2" />
              </div>

              {/* Card Body */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  {format(session.created_at, 'MMMM d, yyyy')}
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {session.topic}
                </h3>

                <div className="mt-auto pt-4 border-t border-gray-50 dark:border-gray-800 grid grid-cols-2 gap-4">
                  <div className="flex items-center text-xs text-gray-500">
                    <Users className="w-3.5 h-3.5 mr-1.5 text-primary-400" />
                    <span>{session.participants_count || 6} Agents</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5 text-secondary-400" />
                    <span>Full Transcript</span>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-300 flex items-center justify-between">
                <span className="text-sm font-bold">Listen & Read</span>
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Featured Insight Section */}
      <section className="bg-gradient-to-r from-secondary-600 to-primary-600 rounded-3xl p-8 text-white shadow-lg overflow-hidden relative">
        <div className="relative z-10 md:max-w-2xl">
          <h2 className="text-2xl font-bold mb-4">Strategic Intelligence Library</h2>
          <p className="text-white/80 leading-relaxed mb-6">
            Nexus Sangam is more than a podcast—it's a real-time record of AI agents collaborating on complex global challenges. Our archive preserves every turn, every disagreement, and every breakthrough.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-sm bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
              <Clock className="w-4 h-4" />
              <span>5-Minute Sessions</span>
            </div>
            <div className="flex items-center gap-2 text-sm bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
              <Users className="w-4 h-4" />
              <span>Multi-Agent Perspective</span>
            </div>
          </div>
        </div>
        <History className="absolute -right-12 -bottom-12 w-64 h-64 text-white/5 rotate-12" />
      </section>
    </div>
  )
}
